import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  authAPI,
  matchAPI,
  chatAPI,
  profileAPI,
  notificationAPI,
} from "../services/api";
import { removeCookie } from "../utils/cookieHelper";
import {
  initSocket,
  setUserId,
  on,
  disconnectSocket,
  joinRoom,
} from "../services/socket";
import { cacheService } from "../services/cache";
import { clearUserCache, removeLocal } from "../utils/storageHelper";

const AuthContext = createContext();
const ACTIVE_MATCH_KEY = "activeMatchId";
const debugLog = () => {};

const normalizeUser = (data) => {
  if (!data) return null;
  const normalized = { ...data };
  const derivedId =
    normalized.userId || normalized._id || normalized.id || normalized.user_id;

  if (derivedId) {
    normalized.userId =
      typeof derivedId === "string"
        ? derivedId
        : derivedId.toString
          ? derivedId.toString()
          : derivedId;
  }

  return normalized;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMatchId, setActiveMatchId] = useState(null);
  const [unreadByMatch, setUnreadByMatch] = useState({});
  const [chatList, setChatList] = useState([]);
  const [chatListLoaded, setChatListLoaded] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const activeMatchIdRef = useRef(null);
  const refreshInFlightRef = useRef(false);
  const lastRefreshAtRef = useRef(0);
  const chatListInFlightRef = useRef(false);
  const processedMessageIdsRef = useRef(new Map());

  const refreshNotificationCount = useCallback(async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      setUnreadNotificationCount(response.data.data?.count || 0);
    } catch (error) {
      console.error("Error fetching notification count:", error);
    }
  }, []);
  // Check if user is logged in on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = sessionStorage.getItem("token");
      if (token) {
        try {
          // Try cache first
          let cachedUser = cacheService.get("currentUser");
          if (cachedUser) {
            const normalizedUser = normalizeUser(cachedUser);
            setUser(normalizedUser);

            if (normalizedUser?.userId !== cachedUser?.userId) {
              cacheService.set("currentUser", normalizedUser, {
                ttl: 10 * 60 * 1000,
                useSessionStorage: true,
              });
            }

            refreshNotificationCount();
          } else {
            const response = await authAPI.getMe();
            const userData = response.data.data;
            const normalizedUser = normalizeUser(userData);
            setUser(normalizedUser);
            // Cache user profile for 10 minutes
            cacheService.set("currentUser", normalizedUser, {
              ttl: 10 * 60 * 1000,
              useSessionStorage: true,
            });
            refreshNotificationCount();
          }
        } catch (error) {
          sessionStorage.removeItem("token");
          cacheService.clear(); // Clear cache on auth failure
          setUnreadNotificationCount(0);
        }
      } else {
        setUser(null);
        setUnreadByMatch({});
        setChatList([]);
        setChatListLoaded(false);
        setActiveMatchId(null);
        setUnreadNotificationCount(0);
        cacheService.clear(); // Clear cache on logout
      }
      setLoading(false);
    };

    initAuth();
  }, [refreshNotificationCount]);

  const refreshUnreadCount = useCallback(async () => {
    const now = Date.now();
    if (refreshInFlightRef.current) return;
    if (now - lastRefreshAtRef.current < 1500) return;
    refreshInFlightRef.current = true;

    try {
      debugLog("[AuthContext] Fetching fresh chat list from backend...");
      const response = await matchAPI.getChatList();
      const freshChatList = response.data.data || [];

      debugLog("[AuthContext] Fresh chat list received:", freshChatList);

      // Cache chat list and unread counts
      cacheService.set("chatList", freshChatList, {
        ttl: 2 * 60 * 1000,
        useSessionStorage: true,
      });

      setUnreadByMatch((prev) => {
        const next = { ...prev };
        freshChatList.forEach((chat) => {
          if (!chat.matchId) return;
          next[chat.matchId] = {
            unreadSpecialCount: chat.unreadSpecialCount || 0,
            unreadNormalCount: chat.unreadNormalCount || 0,
            hasUnreadSpecial: chat.hasUnreadSpecial || false,
            latestMessage: chat.latestMessage,
            lastMessageAt: chat.lastMessageAt,
          };
        });
        debugLog("[AuthContext] Updated unreadByMatch:", next);

        // Cache unread counts
        cacheService.set("unreadCounts", next, {
          ttl: 2 * 60 * 1000,
          useSessionStorage: true,
        });

        return next;
      });

      setChatList(freshChatList);
      debugLog(
        "[AuthContext] ✅ Refreshed unread counts from DB:",
        freshChatList,
      );
    } catch (error) {
      console.error("[AuthContext] Error refreshing unread counts:", error);
    } finally {
      lastRefreshAtRef.current = Date.now();
      refreshInFlightRef.current = false;
    }
  }, []);

  // Initialize socket and set up global listener when user is available
  useEffect(() => {
    if (!user?.userId) {
      return;
    }

    debugLog("[AuthContext] Initializing socket for user:", user.userId);
    initSocket();
    setUserId(user.userId);

    const applyUnreadUpdate = (message, messageMatchId) => {
      if (!message || !messageMatchId) return;

      const messageId = message._id?.toString();
      if (messageId) {
        if (processedMessageIdsRef.current.has(messageId)) return;
        processedMessageIdsRef.current.set(messageId, Date.now());
        if (processedMessageIdsRef.current.size > 200) {
          const oldestKey = processedMessageIdsRef.current.keys().next().value;
          processedMessageIdsRef.current.delete(oldestKey);
        }
      }

      const senderId = message.senderId?._id || message.senderId;
      const receiverId = message.receiverId?._id || message.receiverId;
      const currentUserId = user.userId?.toString();

      if (senderId?.toString() === currentUserId) return;
      if (receiverId?.toString() !== currentUserId) return;

      const currentActiveMatchId = activeMatchIdRef.current;
      if (currentActiveMatchId === messageMatchId) {
        setUnreadByMatch((prev) => {
          const existing = prev[messageMatchId] || {};
          const next = {
            ...prev,
            [messageMatchId]: {
              ...existing,
              unreadSpecialCount: 0,
              unreadNormalCount: 0,
              hasUnreadSpecial: false,
              latestMessage: message,
              lastMessageAt: message.createdAt || new Date().toISOString(),
            },
          };

          cacheService.set("unreadCounts", next, {
            ttl: 2 * 60 * 1000,
            useSessionStorage: true,
          });

          return next;
        });
        return;
      }

      setUnreadByMatch((prev) => {
        const existing = prev[messageMatchId] || {};
        const isSpecial = message.isSpecial === true;

        const next = {
          ...prev,
          [messageMatchId]: {
            ...existing,
            unreadSpecialCount: isSpecial
              ? (existing.unreadSpecialCount || 0) + 1
              : existing.unreadSpecialCount || 0,
            unreadNormalCount: !isSpecial
              ? (existing.unreadNormalCount || 0) + 1
              : existing.unreadNormalCount || 0,
            hasUnreadSpecial: isSpecial || existing.hasUnreadSpecial || false,
            latestMessage: message,
            lastMessageAt: message.createdAt || new Date().toISOString(),
          },
        };

        cacheService.set("unreadCounts", next, {
          ttl: 2 * 60 * 1000,
          useSessionStorage: true,
        });

        return next;
      });
    };

    // Define the global message handler
    const handleGlobalNewMessage = (message) => {
      debugLog(
        "[AuthContext] ⭐ RECEIVED NEW MESSAGE EVENT FROM SOCKET",
        message,
      );

      const messageMatchId = message.matchId?.toString();
      applyUnreadUpdate(message, messageMatchId);
    };

    // Define the global wallet update handler
    const handleWalletUpdated = (data) => {
      debugLog("[AuthContext] 💰 Wallet updated:", data);
      if (data.coins !== undefined) {
        setUser((prev) => ({
          ...prev,
          coins: data.coins,
        }));
      }
    };

    // Register listeners
    // console.log(
    //   "[AuthContext] Setting up global newMessage listener for unread counts",
    // );
    const handleNewMessageNotification = (payload) => {
      const message = payload?.message || payload;
      const messageMatchId =
        payload?.matchId?.toString() || message?.matchId?.toString();
      applyUnreadUpdate(message, messageMatchId);
    };

    const unsubscribeMessage = on("newMessage", handleGlobalNewMessage);
    const unsubscribeMessageNotification = on(
      "newMessageNotification",
      handleNewMessageNotification,
    );
    const unsubscribeWallet = on("walletUpdated", handleWalletUpdated);

    // Cleanup on unmount or when user logs out
    return () => {
      // console.log("[AuthContext] Cleaning up global listeners");
      if (typeof unsubscribeMessage === "function") {
        unsubscribeMessage();
      }
      if (typeof unsubscribeMessageNotification === "function") {
        unsubscribeMessageNotification();
      }
      if (typeof unsubscribeWallet === "function") {
        unsubscribeWallet();
      }
    };
  }, [user, refreshUnreadCount]);
  useEffect(() => {
    if (!user?.userId) return;

    // Fetch initial unread notification count
    refreshNotificationCount();

    // Set up socket listener to refetch when new notifications arrive
    const handleNewNotification = () => {
      setUnreadNotificationCount((prev) => (prev || 0) + 1);
      refreshNotificationCount();
    };

    const unsubscribeNotification = on(
      "newNotification",
      handleNewNotification,
    );

    return () => {
      if (typeof unsubscribeNotification === "function") {
        unsubscribeNotification();
      }
    };
  }, [user?.userId, refreshNotificationCount]);

  useEffect(() => {
    activeMatchIdRef.current = activeMatchId ? activeMatchId.toString() : null;
  }, [activeMatchId]);

  useEffect(() => {
    if (activeMatchId) {
      localStorage.setItem(ACTIVE_MATCH_KEY, activeMatchId.toString());
    } else {
      localStorage.removeItem(ACTIVE_MATCH_KEY);
    }
  }, [activeMatchId]);

  const markMatchRead = useCallback(async (matchId) => {
    if (!matchId) return;

    setUnreadByMatch((prev) => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        unreadSpecialCount: 0,
        unreadNormalCount: 0,
        hasUnreadSpecial: false,
      },
    }));

    try {
      await chatAPI.markAsRead(matchId);
    } catch (error) {
      console.error("Error marking messages read:", error);
    }
  }, []);

  const loadChatList = useCallback(async () => {
    if (chatListInFlightRef.current) return;
    chatListInFlightRef.current = true;

    try {
      const response = await matchAPI.getChatList();
      const nextChatList = response.data.data || [];
      setChatList(nextChatList);

      setUnreadByMatch((prev) => {
        const next = { ...prev };
        nextChatList.forEach((chat) => {
          if (!chat.matchId) return;
          next[chat.matchId] = {
            ...(next[chat.matchId] || {}),
            unreadSpecialCount: chat.unreadSpecialCount || 0,
            unreadNormalCount: chat.unreadNormalCount || 0,
            hasUnreadSpecial: chat.hasUnreadSpecial || false,
            latestMessage: chat.latestMessage,
            lastMessageAt: chat.lastMessageAt,
          };
        });
        return next;
      });

      nextChatList.forEach((chat) => {
        if (chat.matchId) {
          joinRoom(chat.matchId);
          debugLog("[AuthContext] Joined room:", chat.matchId);
        }
      });
    } catch (error) {
      console.error("Error loading chat list:", error);
    } finally {
      setChatListLoaded(true);
      chatListInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    // Load chat list
    loadChatList();
  }, [user, loadChatList]);

  const signup = useCallback(
    async (email, password) => {
      try {
        const response = await authAPI.signup({ email, password });
        const { token, ...userData } = response.data.data;
        sessionStorage.setItem("token", token);
        const normalizedUser = normalizeUser(userData);
        setUser(normalizedUser);
        cacheService.set("currentUser", normalizedUser, {
          ttl: 10 * 60 * 1000,
          useSessionStorage: true,
        });
        refreshNotificationCount();
        return { success: true };
      } catch (error) {
        return {
          success: false,
          message: error.response?.data?.message || "Signup failed",
        };
      }
    },
    [refreshNotificationCount],
  );

  const login = useCallback(
    async (email, password) => {
      try {
        const response = await authAPI.login({ email, password });
        const { token, ...userData } = response.data.data;
        sessionStorage.setItem("token", token);
        const normalizedUser = normalizeUser(userData);
        setUser(normalizedUser);
        cacheService.set("currentUser", normalizedUser, {
          ttl: 10 * 60 * 1000,
          useSessionStorage: true,
        });
        refreshNotificationCount();
        return { success: true };
      } catch (error) {
        return {
          success: false,
          message: error.response?.data?.message || "Login failed",
        };
      }
    },
    [refreshNotificationCount],
  );

  const googleLogin = useCallback(
    async (idToken) => {
      try {
        const response = await authAPI.googleLogin({ idToken });
        const { token, ...userData } = response.data.data;
        sessionStorage.setItem("token", token);
        const normalizedUser = normalizeUser(userData);
        setUser(normalizedUser);
        cacheService.set("currentUser", normalizedUser, {
          ttl: 10 * 60 * 1000,
          useSessionStorage: true,
        });
        refreshNotificationCount();
        return { success: true };
      } catch (error) {
        return {
          success: false,
          message: error.response?.data?.message || "Google login failed",
        };
      }
    },
    [refreshNotificationCount],
  );

  const forgotPassword = useCallback(async (email) => {
    try {
      await authAPI.forgotPassword({ email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Request failed",
      };
    }
  }, []);

  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      await authAPI.resetPassword({ token, newPassword });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Reset failed",
      };
    }
  }, []);

  const logout = useCallback(() => {
    // Clear all storage
    sessionStorage.removeItem("token");
    removeCookie("activeMatchId");

    // Clear localStorage data
    clearUserCache();
    removeLocal("discovery_profile");
    removeLocal("user_profile");
    removeLocal("user_matches");
    removeLocal("active_match_id");
    removeLocal("chat_messages");

    // Disconnect socket and reset state
    disconnectSocket();
    setUser(null);
    setUnreadByMatch({});
    setChatList([]);
    setChatListLoaded(false);
    setActiveMatchId(null);
    setUnreadNotificationCount(0);
    cacheService.clear(); // Clear cache service

    console.log("[Auth] User logged out, all data cleared");
  }, []);

  const deleteAccount = useCallback(async () => {
    try {
      await profileAPI.deleteProfile();
      // Clear all state and disconnect
      sessionStorage.removeItem("token");
      removeCookie("activeMatchId");

      // Clear localStorage data
      clearUserCache();
      removeLocal("discovery_profile");
      removeLocal("user_profile");
      removeLocal("user_matches");
      removeLocal("active_match_id");
      removeLocal("chat_messages");

      disconnectSocket();
      setUser(null);
      setUnreadByMatch({});
      setChatList([]);
      setChatListLoaded(false);
      setActiveMatchId(null);
      setUnreadNotificationCount(0);
      cacheService.clear(); // Clear cache on account deletion

      console.log("[Auth] Account deleted, all data cleared");
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to delete account",
      };
    }
  }, []);

  const updateUser = useCallback((userData) => {
    setUser((prev) => ({ ...prev, ...userData }));
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      signup,
      login,
      googleLogin,
      forgotPassword,
      resetPassword,
      logout,
      deleteAccount,
      updateUser,
      activeMatchId,
      setActiveMatchId,
      unreadByMatch,
      markMatchRead,
      chatList,
      chatListLoaded,
      unreadNotificationCount,
      refreshNotificationCount,
    }),
    [
      user,
      loading,
      signup,
      login,
      googleLogin,
      forgotPassword,
      resetPassword,
      logout,
      deleteAccount,
      updateUser,
      activeMatchId,
      setActiveMatchId,
      unreadByMatch,
      markMatchRead,
      chatList,
      chatListLoaded,
      unreadNotificationCount,
      refreshNotificationCount,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
